import { useState } from "react";
import { createPortal } from "react-dom";
import styled, { css } from "styled-components";
import { ModalBody, ModalContent, ModalOverlay } from "../../contents/PrivacyPolicyModal";

// '상담 요청' 버튼. 기본은 Contact Point 아래(이 페이지 담당자 앞), expert를 주면 그 전문가 한 명 앞(구성원 카드).
// 프로토타입: 입력 내용을 담아 담당자 앞으로 메일 작성 창(mailto)을 연다.
// 운영 시: 상담 접수 API로 보내 담당 부서 배정·회신 추적(CRM)으로 연결.
const ConsultRequest = ({ members = [], label = "이 분야 전문가에게 상담 요청", expert, compact = false }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ company: "", name: "", contact: "", content: "" });
  const to = members
    .filter((m) => m && m.id)
    .map((m) => m.email || `${m.id}@aonecustoms.com`)
    .join(",");
  const topic = expert ? `${expert.name} ${expert.grade}` : (document.title || "").split(" - ")[0];

  const submit = (e) => {
    e.preventDefault();
    const subject = `[홈페이지 상담 요청] ${topic} - ${form.company}`;
    const body = `회사명: ${form.company}\n담당자: ${form.name}\n연락처: ${form.contact}\n${expert ? "담당 전문가" : "문의 분야"}: ${topic}\n\n${form.content}`;
    window.location.href = `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    setOpen(false);
  };
  const set = (k) => (e) => setForm((prev) => ({ ...prev, [k]: e.target.value }));

  return (
    <>
      <Button type="button" $compact={compact} onClick={() => setOpen(true)}>
        {label}
      </Button>
      {open &&
        // 카드 안에서 열어도 카드의 hover·overflow 스타일에 묶이지 않도록 body에 띄운다
        createPortal(
          <ModalOverlay onClick={() => setOpen(false)}>
            <ModalContent role="dialog" aria-modal="true" aria-label="상담 요청" onClick={(e) => e.stopPropagation()}>
              <ModalBody>
                <Form onSubmit={submit}>
                  {/* 번역 사전이 문구별로 찾도록 '상담 요청'·이름·직함을 따로 둔다 */}
                  <h3>
                    {"상담 요청"} · {expert ? <>{expert.name} {expert.grade}</> : topic}
                  </h3>
                  <p>{expert ? "남겨주신 내용은 선택한 전문가에게 전달됩니다." : "남겨주신 내용은 이 페이지의 담당 관세사에게 전달됩니다."}</p>
                  <label>회사명<input required value={form.company} onChange={set("company")} /></label>
                  <label>담당자 성함<input required value={form.name} onChange={set("name")} /></label>
                  <label>연락처(이메일 또는 전화)<input required value={form.contact} onChange={set("contact")} /></label>
                  <label>문의 내용<textarea required rows={5} value={form.content} onChange={set("content")} /></label>
                  <div className="buttons">
                    <button type="button" onClick={() => setOpen(false)}>취소</button>
                    <button type="submit">메일로 보내기</button>
                  </div>
                </Form>
              </ModalBody>
            </ModalContent>
          </ModalOverlay>,
          document.body
        )}
    </>
  );
};
export default ConsultRequest;

const Button = styled.button`
  font: inherit;
  cursor: pointer;
  margin-top: 2.5rem;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  border: none;
  border-radius: 999px;
  background-color: ${({ theme }) => theme.colors.dark};
  color: white;
  font-weight: 700;
  padding: 0.95rem 2rem;
  box-shadow: ${({ theme }) => theme.shadow.sm};
  transition: background-color 0.2s, color 0.2s;
  &:after {
    content: "→";
  }
  &:hover {
    background-color: ${({ theme }) => theme.colors.accent};
  }
  /* 구성원 카드 안의 작은 버튼 */
  ${({ $compact, theme }) =>
    $compact &&
    css`
      align-self: flex-start;
      margin-top: 1.25rem;
      padding: 0.55rem 1.1rem;
      font-size: 0.9rem;
      background-color: ${theme.colors.accentSoft};
      color: ${theme.colors.accent};
      box-shadow: none;
      &:hover {
        color: white;
      }
    `}
`;
const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
  min-width: min(30rem, 80vw);
  & > h3 {
    margin: 0;
    font-size: 1.35rem;
    color: ${({ theme }) => theme.colors.dark};
  }
  & > p {
    margin: 0 0 0.25rem;
    color: ${({ theme }) => theme.colors.foreground.dimmed};
  }
  & > label {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    font-size: 0.9rem;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.monotone.dark};
    & > input,
    & > textarea {
      font: inherit;
      font-size: 1rem;
      font-weight: normal;
      padding: 0.7rem 0.9rem;
      border-radius: 0.6rem;
      border: 1px solid ${({ theme }) => theme.colors.monotone.light};
      &:focus {
        outline: none;
        border-color: ${({ theme }) => theme.colors.accent};
        box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.accentSoft};
      }
    }
  }
  & > .buttons {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
    margin-top: 0.5rem;
    & > button {
      font: inherit;
      font-weight: 600;
      cursor: pointer;
      padding: 0.7rem 1.4rem;
      border-radius: 999px;
      border: 1px solid ${({ theme }) => theme.colors.monotone.light};
      background: white;
      color: ${({ theme }) => theme.colors.monotone.dark};
      &:last-child {
        border-color: ${({ theme }) => theme.colors.dark};
        background: ${({ theme }) => theme.colors.dark};
        color: white;
      }
    }
  }
`;
